import type { TPageTranslations } from '@crehana/i18n';

import { PAGE_KEY } from './constants';
import type { TKeys } from './types';

export const pt: TPageTranslations<TKeys, typeof PAGE_KEY> = {
  [PAGE_KEY]: {
    SEO_TITLE: 'Solicitações | Presença | Crehana',
    SEO_DESCRIPTION:
      'Revise e responda às solicitações de horas extras e tempo de compensação dos seus colaboradores.',

    BREADCRUMB_HOME: 'Início',
    BREADCRUMB_ATTENDANCE: 'Presença',
    BREADCRUMB_REQUESTS: 'Solicitações',

    PAGE_TITLE: 'Solicitações',
    PAGE_DESCRIPTION:
      'Revise e responda às solicitações de horas extras e tempo de compensação dos seus colaboradores.',

    TABLE_COLUMN_DATE: 'Data',
    TABLE_COLUMN_USER: 'Usuário',
    TABLE_COLUMN_SHIFT: 'Turno',
    TABLE_COLUMN_OVERTIME: 'Horas extras',
    TABLE_COLUMN_COMPENSATION: 'Tempo de compensação',
    TABLE_COLUMN_REASON: 'Motivo',
    TABLE_COLUMN_STATUS: 'Status',

    REQUEST_STATUS_PENDING: 'Pendente',
    REQUEST_STATUS_APPROVED: 'Aprovada',
    REQUEST_STATUS_DENIED: 'Negada',
    REQUEST_STATUS_UPDATED_TOOLTIP:
      'O tempo final pode ser diferente do solicitado, pois corresponde ao tempo registrado durante a jornada e a possíveis ajustes feitos pelo administrador.',

    REQUESTS_ACTION_VIEW_DETAIL: 'Ver detalhe',
    REQUESTS_ACTION_RESPOND: 'Responder solicitação',

    REQUESTS_EMPTY_TITLE: 'Ainda não há solicitações',
    REQUESTS_EMPTY_DESCRIPTION:
      'As solicitações de horas extras e reposição da sua equipe vão aparecer aqui.',
    REQUESTS_NO_RESULTS_TITLE: 'Não há resultados para mostrar',
    REQUESTS_NO_RESULTS_DESCRIPTION: 'Tente novamente com um filtro diferente.',

    FILTER_BUTTON_LABEL: 'Filtrar por',
    FILTER_ALL: 'Todos',
    FILTER_PANEL_LEGAL_ENTITY_LABEL: 'Razão Social',
    FILTER_PANEL_GROUP_LABEL: 'Grupos',
    FILTER_PANEL_POSITION_LABEL: 'Cargo',
    FILTER_PANEL_SHIFT_LABEL: 'Turno',
    FILTER_PANEL_LOCATION_LABEL: 'Local de marcação',
    FILTER_PANEL_DEVICE_LABEL: 'Dispositivos',
    FILTER_PANEL_CLEAR: 'Limpar',
    FILTER_DATE_FROM_LABEL: 'De',
    FILTER_DATE_TO_LABEL: 'Até',
    FILTER_DATE_PLACEHOLDER: 'dd/mm/aaaa',

    ACTIONS_MENU_LABEL: 'Mais opções',
    ACTIONS_MENU_CANCEL: 'Cancelar',

    ERROR_TITLE: 'Algo deu errado',
    ERROR_DESCRIPTION:
      'Não foi possível carregar as solicitações. Tente novamente.',
    BUTTON_RETRY: 'Tentar novamente',
    LIST_FORBIDDEN_TITLE: 'Você não tem acesso a estas solicitações',
    LIST_FORBIDDEN_DESCRIPTION:
      'Peça a um administrador a permissão para ver as solicitações da sua equipe.',
    LIST_SCOPE_TOO_LARGE_DESCRIPTION:
      'A razão social escolhida tem colaboradores demais. Restrinja a busca com outro filtro.',

    // Fuentes de marcaje del filtro Dispositivos (TimeMarkSource)
    DEVICE_SOURCE_APP: 'App Móvel',
    DEVICE_SOURCE_WEB: 'Web',
    DEVICE_SOURCE_BIOMETRIC: 'Biométrico',
    DEVICE_SOURCE_EXCEL: 'Carga em massa',
    DEVICE_SOURCE_MANUAL: 'Manual',
    DEVICE_SOURCE_SYSTEM: 'Sistema',

    // TIM-565 — drawer "Ver detalhe"
    REQUEST_DETAIL_DRAWER_TITLE: 'Solicitação de {{date}}',
    REQUEST_DETAIL_TITLE_DATE_PATTERN: "d 'de' MMMM",
    REQUEST_DETAIL_CREATED_AT_LABEL: 'Data da solicitação',
    REQUEST_DETAIL_OVERTIME_REQUESTED_LABEL: 'Horas extras solicitadas',
    REQUEST_DETAIL_COMPENSATION_REQUESTED_LABEL:
      'Tempo de compensação solicitado',
    REQUEST_DETAIL_OVERTIME_FINAL_LABEL: 'Horas extras finais',
    REQUEST_DETAIL_COMPENSATION_FINAL_LABEL: 'Tempo de compensação final',
    REQUEST_DETAIL_RESPONDED_BY_LABEL: 'Respondido por',
    REQUEST_DETAIL_COMMENTS_SECTION_TITLE: 'Comentários',
    REQUEST_DETAIL_COMMENTS_THREAD_TITLE: 'Tópico',
    REQUEST_DETAIL_COMMENTS_EMPTY: 'Ainda não há disponíveis',
    REQUEST_DETAIL_COMMENTS_EMPTY_SUBTITLE:
      'Os comentários que você adicionar sobre esta solicitação vão aparecer aqui.',
    REQUEST_DETAIL_COMMENTS_ERROR_DESCRIPTION:
      'Não foi possível carregar os comentários. Tente novamente.',
    REQUEST_DETAIL_COMMENT_LABEL: 'Adicionar comentário',
    REQUEST_DETAIL_COMMENT_PLACEHOLDER:
      'Use este espaço para adicionar comentários sobre esta solicitação.',
    REQUEST_DETAIL_COMMENT_SUBMIT: 'Adicionar comentário',
    VALIDATION_COMMENT_REQUIRED: 'Campo obrigatório.',
    VALIDATION_COMMENT_MAX_LENGTH: 'Máximo de {{max}} caracteres.',
    REQUEST_DETAIL_COMMENTS_FORBIDDEN_TITLE:
      'Você não tem acesso a esta solicitação',
    REQUEST_DETAIL_COMMENTS_FORBIDDEN_DESCRIPTION:
      'Ela pode ter sido anulada ou você pode não ter permissão para visualizá-la.',
    REQUEST_DETAIL_COMMENT_ERROR_FORBIDDEN:
      'Você não tem acesso para comentar nesta solicitação.',
    REQUEST_DETAIL_COMMENT_ERROR_GENERIC:
      'Não foi possível adicionar seu comentário. Tente novamente.',

    // TIM-562 — dialog "Responder solicitação"
    RESPOND_DIALOG_TITLE: 'Responder solicitação',
    RESPOND_DIALOG_PROMPT:
      'Escolha a seguir como deseja responder à solicitação:',
    RESPOND_DIALOG_APPROVE: 'Aprovar',
    RESPOND_DIALOG_DENY: 'Negar',
    RESPOND_DIALOG_SURPLUS_AVAILABLE_LABEL: 'Tempo excedente',
    RESPOND_DIALOG_SURPLUS_UNAVAILABLE: 'Ainda não disponível',
    RESPOND_DIALOG_REQUESTED_OVERTIME_LABEL: 'Horas extras solicitadas',
    RESPOND_DIALOG_REQUESTED_COMPENSATION_LABEL:
      'Tempo de compensação solicitado',
    RESPOND_DIALOG_REASON_LABEL: 'Motivo da resposta',
    RESPOND_DIALOG_REASON_PLACEHOLDER: 'Indique o motivo da resposta',
    RESPOND_DIALOG_NO_SHIFT_BLOCK:
      'Antes de aprovar esta solicitação, atribua um turno a {{name}} para {{date}}.',
    RESPOND_DIALOG_NO_SHIFT_DATE_PATTERN: "d 'de' MMMM 'de' yyyy",
    RESPOND_DIALOG_CONFIRM_APPROVE: 'Confirmar aprovação',
    RESPOND_DIALOG_CONFIRM_DENY: 'Confirmar negação',
    VALIDATION_REASON_REQUIRED: 'Campo obrigatório.',
    VALIDATION_REASON_TOO_LONG: 'Máximo de {{max}} caracteres.',
    TOAST_REQUEST_APPROVED: 'A solicitação foi aprovada.',
    TOAST_REQUEST_DENIED: 'A solicitação foi negada.',
    TOAST_REQUEST_CONFLICT:
      'Não é possível responder a esta solicitação, parece que outra pessoa já a resolveu.',
    TOAST_RESPOND_ERROR:
      'Não foi possível processar sua solicitação. Tente novamente.',

    // TIM-602 — gate por permissão
    RESPOND_NO_PERMISSION:
      'Você não tem permissão para responder solicitações.',
  },
};
